/**
 * 短劇卡司（dramas.actors）排序與狀態 helpers. Pure — no React / Firebase — so node:test can import them.
 *
 * dramas.actors 的陣列順序就是 App 卡司順序（第一位＝主演）；App 只顯示 artistProfiles 已公開的藝人
 * （functions/src/handlers/drama-cast.ts），未公開的會被默默略過。
 */
import { PROFILE_STATUS_LABELS, type ArtistAdminRow } from "./artistAdmin";

export type CastStatusTone = "success" | "warning" | "neutral" | "purple";
export interface CastStatusChip {
  label: string;
  tone: CastStatusTone;
}

/** Appends an id (ignored when empty or already in the cast). */
export function addCastMember(ids: readonly string[], id: string): string[] {
  if (!id || ids.includes(id)) return [...ids];
  return [...ids, id];
}

export function removeCastMember(ids: readonly string[], id: string): string[] {
  return ids.filter((x) => x !== id);
}

/** Moves the item at `index` by `delta` (−1 = up, +1 = down); out-of-range moves return an unchanged copy. */
export function moveCastMember(ids: readonly string[], index: number, delta: number): string[] {
  const next = [...ids];
  const target = index + delta;
  if (index < 0 || index >= next.length || target < 0 || target >= next.length || delta === 0) return next;
  const [item] = next.splice(index, 1);
  next.splice(target, 0, item);
  return next;
}

/** 0 → 主演, n → 第 n+1 位. Negative (not in cast) → empty string. */
export function castPositionLabel(index: number): string {
  if (index < 0) return "";
  return index === 0 ? "主演" : `第 ${index + 1} 位`;
}

/** Status chips for one artist; no row (status unknown / hub off) → no chips. */
export function castStatusChips(row: Pick<ArtistAdminRow, "profileStatus" | "verified"> | undefined): CastStatusChip[] {
  if (!row) return [];
  const chips: CastStatusChip[] = [];
  const label = PROFILE_STATUS_LABELS[row.profileStatus];
  if (row.profileStatus === "published") chips.push({ label, tone: "success" });
  else if (row.profileStatus === "unpublished") chips.push({ label, tone: "warning" });
  else chips.push({ label, tone: "neutral" });
  if (row.verified) chips.push({ label: "已認證", tone: "purple" });
  return chips;
}

/** Cast ids whose status is known and not published (they will not appear in the App cast), in cast order. */
export function hiddenFromAppCast(
  ids: readonly string[],
  statusById: ReadonlyMap<string, Pick<ArtistAdminRow, "profileStatus">>,
): string[] {
  return ids.filter((id) => {
    const row = statusById.get(id);
    return row !== undefined && row.profileStatus !== "published";
  });
}

/** 「N 位藝人的專頁未公開，不會出現在 App 卡司：A、B」, or null when none. */
export function hiddenCastWarning(names: readonly string[]): string | null {
  if (names.length === 0) return null;
  return `${names.length} 位藝人的專頁未公開，不會出現在 App 卡司：${names.join("、")}`;
}
